import React, { useState } from "react";
import { SlidersHorizontal, X, Check } from "lucide-react";

// Sales Order filters (Oct 2026). Sits under the Product Family and
// Category rows. "Filter" opens Brand and Size choices (with counts, from
// the server); the active choices stay visible as removable chips with a
// "Clear all filters" link. Every result remains one exact product card —
// nothing here selects a variant inside a card.
//
// Renders nothing when the products in scope carry no Brand and no Size
// (a restaurant menu), so no empty, useless filter is ever shown.
function Choice({ label, count, checked, onToggle, testId }) {
  const disabled = !checked && count === 0;
  return (
    <label
      className={
        "flex items-center gap-3 min-h-[44px] px-2 rounded-md cursor-pointer select-none " +
        (disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-emerald-900/5")
      }
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        data-testid={testId}
      />
      <span
        aria-hidden="true"
        className={
          "w-5 h-5 shrink-0 rounded border grid place-items-center " +
          (checked ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-stone-400")
        }
      >
        {checked && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
      </span>
      <span className="text-sm text-stone-900">{label}</span>
      <span className="text-xs text-stone-500">({count})</span>
    </label>
  );
}

function ActiveChip({ label, onClear, testId }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-white border border-emerald-900/20 pl-3 pr-1 py-1 text-xs text-stone-800">
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label={`Remove filter ${label}`}
        data-testid={testId}
        className="w-7 h-7 grid place-items-center rounded-full hover:bg-stone-100"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </span>
  );
}

export default function CatalogFilterBar({ filters }) {
  const [open, setOpen] = useState(false);
  if (!filters || (!filters.available && !filters.active)) return null;
  const { facets, selection } = filters;
  const isSizeChecked = (value) => selection.sizes.some((s) => s.value === value);

  return (
    <section className="shrink-0 bg-[#e8efd0] border-b border-emerald-900/20 px-3 sm:px-4 py-2" data-testid="retail-filter-bar">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="retail-filter-panel"
          data-testid="retail-filter-toggle"
          className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-md bg-white border border-emerald-900/25 text-sm font-semibold text-emerald-900"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filter
          {filters.activeCount > 0 && (
            <span className="ml-0.5 min-w-[20px] h-5 px-1 rounded-full bg-orange-500 text-white text-[11px] grid place-items-center" data-testid="retail-filter-count">
              {filters.activeCount}
            </span>
          )}
        </button>

        {selection.category && (
          <ActiveChip label={`Category: ${selection.category}`} onClear={() => filters.setCategory(null)}
            testId="retail-filter-clear-category" />
        )}
        {selection.brands.map((b) => (
          <ActiveChip key={`b-${b}`} label={`Brand: ${b}`} onClear={() => filters.toggleBrand(b)}
            testId={`retail-filter-clear-brand-${b}`} />
        ))}
        {selection.sizes.map((s) => (
          <ActiveChip key={`s-${s.value}`} label={`Size: ${s.label}`} onClear={() => filters.toggleSize(s)}
            testId={`retail-filter-clear-size-${s.label}`} />
        ))}
        {filters.active && (
          <button
            type="button"
            onClick={filters.clearAll}
            data-testid="retail-filter-clear-all"
            className="text-xs font-semibold text-orange-700 underline underline-offset-2 min-h-[40px] px-1"
          >
            Clear all filters
          </button>
        )}
        {filters.active && (
          <span className="text-xs text-emerald-900/80 ml-auto" role="status" data-testid="retail-filter-total">
            {filters.loading ? "Loading…" : `${filters.total} product${filters.total === 1 ? "" : "s"} found`}
          </span>
        )}
      </div>

      {open && (
        <div
          id="retail-filter-panel"
          data-testid="retail-filter-panel"
          className="mt-2 rounded-lg bg-white border border-emerald-900/15 p-2 max-h-[60vh] overflow-y-auto"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {facets.brands.length > 0 && (
              <fieldset>
                <legend className="px-2 pb-1 text-xs font-bold uppercase tracking-wide text-emerald-900">Brand</legend>
                {facets.brands.map((b) => (
                  <Choice
                    key={b.value}
                    label={b.value}
                    count={b.count}
                    checked={selection.brands.includes(b.value)}
                    onToggle={() => filters.toggleBrand(b.value)}
                    testId={`retail-filter-brand-${b.value}`}
                  />
                ))}
              </fieldset>
            )}
            {facets.sizes.length > 0 && (
              <fieldset>
                <legend className="px-2 pb-1 text-xs font-bold uppercase tracking-wide text-emerald-900">Size</legend>
                {facets.sizes.map((s) => (
                  <Choice
                    key={s.value}
                    label={s.label}
                    count={s.count}
                    checked={isSizeChecked(s.value)}
                    onToggle={() => filters.toggleSize(s)}
                    testId={`retail-filter-size-${s.label}`}
                  />
                ))}
              </fieldset>
            )}
          </div>
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="retail-filter-done"
              className="min-h-[40px] px-4 rounded-md bg-orange-500 text-white text-sm font-semibold"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
